import type { ApiDashboardElement, SourceMap } from '@highimpact-frontend/api-types';
import {
  DEFAULT_DECIMATION_POLICY,
  buildRequestKey,
  resolveDefaultBounds,
  type DecimationPolicy,
} from '@highimpact/react-hooks';
import { useEffect, useState } from 'react';

import { useSourceLabelsQuery } from '../../../../query/useSourceLabelsQuery';
import type { MeasureRow } from '../../../../runtime/measureRow.types';
import { getIntervals } from '../../../../state/dashboard-element-operations';
import {
  useElementMeasureTransport,
  type ElementMeasurePlotType,
} from './useElementMeasureTransport';

export interface UseStaticElementMeasureRowsOptions {
  readonly element: ApiDashboardElement;
  readonly plotType?: ElementMeasurePlotType;
  readonly decimationPolicy?: DecimationPolicy;
  readonly sourceLabelMap?: SourceMap;
}

export interface UseStaticElementMeasureRowsResult {
  readonly rows: MeasureRow[] | null;
  readonly loading: boolean;
  readonly error: Error | null;
  readonly usesElementMeasureRequest: boolean;
  readonly labelMap: SourceMap;
}

/**
 * Loads a single measure response for plots that do not use range/zoom transport.
 */
export function useStaticElementMeasureRows({
  element,
  plotType,
  decimationPolicy = DEFAULT_DECIMATION_POLICY,
  sourceLabelMap: sourceLabelMapOverride,
}: UseStaticElementMeasureRowsOptions): UseStaticElementMeasureRowsResult {
  const {
    labelMap: queriedLabelMap,
    loading: labelsLoading,
    error: labelsError,
  } = useSourceLabelsQuery(element);
  const sourceLabelMap = sourceLabelMapOverride ?? queriedLabelMap;

  const {
    transport,
    measureRequestSignature,
    usesElementMeasureRequest,
    effectiveDecimationPolicy,
  } = useElementMeasureTransport({
    element,
    plotType,
    decimationPolicy,
    sourceLabelMap,
  });

  const [rows, setRows] = useState<MeasureRow[] | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<Error | null>(null);

  useEffect(() => {
    if (!usesElementMeasureRequest || labelsLoading) {
      if (!usesElementMeasureRequest) {
        setRows(null);
        setFetchLoading(false);
        setFetchError(null);
      }
      return;
    }

    const intervals = getIntervals(element) ?? [];
    const bounds = resolveDefaultBounds(intervals);
    if (!bounds) {
      setRows([]);
      setFetchLoading(false);
      setFetchError(null);
      return;
    }

    let cancelled = false;
    setFetchLoading(true);
    setFetchError(null);

    void transport
      .fetch({
        type: 'with-cache',
        slot: 'overview',
        timeRange: bounds,
        decimation: effectiveDecimationPolicy,
        replace: true,
        requestKey: `${buildRequestKey('overview', bounds, effectiveDecimationPolicy)}:${measureRequestSignature ?? 'static'}`,
      })
      .then((data) => {
        if (!cancelled) {
          setRows(data);
          setFetchLoading(false);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setFetchError(
            error instanceof Error ? error : new Error(String(error))
          );
          setFetchLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    decimationPolicy,
    effectiveDecimationPolicy,
    element,
    labelsLoading,
    measureRequestSignature,
    plotType,
    transport,
    usesElementMeasureRequest,
  ]);

  return {
    rows,
    loading: labelsLoading || fetchLoading,
    error: labelsError ?? fetchError,
    usesElementMeasureRequest,
    labelMap: sourceLabelMap,
  };
}
